import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';

function PayPalSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');
  const [currentSubscription, setCurrentSubscription] = useState(0);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);

  useEffect(() => {
    const handleSubscriptionChain = async () => {
      const urlParams = new URLSearchParams(location.search);
      const companyId = urlParams.get('company_id');
      const subscriptionIndex = parseInt(urlParams.get('subscription_index') || '0');
      const totalSubs = parseInt(urlParams.get('total_subscriptions') || '1');
      
      setCurrentSubscription(subscriptionIndex + 1); // Display as 1-based
      setTotalSubscriptions(totalSubs);

      if (!companyId) {
        setStatus('error');
        return;
      }

      // Check if there are more subscriptions to approve
      if (subscriptionIndex + 1 < totalSubs) {
        setStatus('continuing');
        
        // Wait 3 seconds, then redirect to next subscription
        setTimeout(async () => {
          try {
            // Get company data to find next subscription URL
            const companyDoc = await getDoc(doc(db, 'companies', companyId));
            const companyData = companyDoc.data();
            
            if (companyData?.subscriptions) {
              // Find the next subscription that needs approval
              const subscriptions = Object.entries(companyData.subscriptions);
              const nextSubscription = subscriptions.find(([id, sub]) => 
                sub.subscriptionIndex === subscriptionIndex + 1 && sub.status === 'APPROVAL_PENDING'
              );
              
              if (nextSubscription && nextSubscription[1].approvalUrl) {
                // Redirect to next PayPal approval
                window.location.href = nextSubscription[1].approvalUrl;
                return;
              }
            }
            
            // If we can't find the next subscription, go to account settings
            navigate('/account-settings?message=Some subscriptions may need manual approval');
          } catch (error) {
            console.error('Error finding next subscription:', error);
            navigate('/account-settings?message=Please check your subscription status');
          }
        }, 3000);
      } else {
        // This was the last subscription
        setStatus('completed');
        setTimeout(() => {
          navigate('/account-settings?message=All subscriptions completed successfully!');
        }, 3000);
      }
    };

    handleSubscriptionChain();
  }, [navigate, location.search]);

  const getStatusContent = () => {
    switch (status) {
      case 'continuing':
        return {
          title: '✅ Subscription Approved!',
          message: `Subscription ${currentSubscription} of ${totalSubscriptions} completed successfully.`,
          subMessage: 'Redirecting to next subscription approval...',
          bgColor: '#ff9800'
        };
      case 'completed':
        return {
          title: '🎉 All Subscriptions Complete!',
          message: `All ${totalSubscriptions} subscriptions have been approved successfully!`,
          subMessage: 'Redirecting to your account...',
          bgColor: '#4caf50'
        };
      case 'error':
        return {
          title: '❌ Error',
          message: 'There was an error processing your subscription.',
          subMessage: 'Please contact support if this continues.',
          bgColor: '#f44336'
        };
      default:
        return {
          title: '⏳ Processing...',
          message: 'Processing your subscription approval...',
          subMessage: 'Please wait...',
          bgColor: '#2196f3'
        };
    }
  };

  const content = getStatusContent();

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100vh', 
      background: '#2d3748', 
      color: '#e0e0e0',
      fontFamily: 'Poppins, sans-serif',
      textAlign: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: content.bgColor,
        color: 'white',
        padding: '30px',
        borderRadius: '10px',
        maxWidth: '600px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ margin: '0 0 20px 0', fontSize: '2.5em' }}>{content.title}</h1>
        <p style={{ fontSize: '1.2em', margin: '0 0 20px 0' }}>
          {content.message}
        </p>
        {totalSubscriptions > 1 && status !== 'error' && (
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            padding: '15px', 
            borderRadius: '5px',
            margin: '20px 0'
          }}>
            <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '10px' }}>
              Progress: {currentSubscription} of {totalSubscriptions} subscriptions
            </div>
            <div style={{ 
              background: 'rgba(255,255,255,0.3)', 
              height: '10px', 
              borderRadius: '5px',
              overflow: 'hidden'
            }}>
              <div style={{
                background: 'white',
                height: '100%',
                width: `${(currentSubscription / totalSubscriptions) * 100}%`,
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        )}
        <div style={{ 
          background: 'rgba(255,255,255,0.1)', 
          padding: '15px', 
          borderRadius: '5px',
          margin: '20px 0'
        }}>
          <p style={{ margin: '0', fontSize: '0.9em' }}>
            {content.subMessage}
          </p>
        </div>
        {status === 'error' && (
          <button 
            onClick={() => navigate('/account-settings')}
            style={{
              background: '#2196F3',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '5px',
              fontSize: '16px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            Go to Account Settings
          </button>
        )}
      </div>
    </div>
  );
}

export default PayPalSuccess;